import { useEffect, useState } from 'react';
import { Button, Col, Divider, Row } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

// services
import NotificationService from 'core/services/notification';
import { isRTL } from 'core/services/language';

// components
import {
	onApproveIncompetent,
	onDeleteIncompetent,
	onGetIncompetentById,
	onRejectIncompetent,
	onUnlinkIncompetent,
} from 'modules/incompetents/services/incompetents';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import AuthActionComponent from 'components/authAction/authAction';
import FilePreview from 'components/filePreview/filePreview';
import BackButton from 'components/backButton/backButton';
import Icon from 'utils/icon';

// models
import { Incompetent } from 'modules/incompetents/models/incompetents';

//styles
import styles from './view.module.scss';
import PDFViewer from 'components/pdfViewer/pdfViewer';
import { StatusColors } from 'utils/statusColors';

function IncompetentViewPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [incompetentDetails, setIncompetentDetails] = useState<Incompetent>();

	// actions states
	const [isUnlinking, setIsUnlinking] = useState(false);
	const [isApproving, setIsApproving] = useState(false);
	const [isRejecting, setIsRejecting] = useState(false);

	const [showUnlinkingConfirmation, setShowUnlinkingConfirmation] = useState(false);
	const [showApprovingConfirmation, setShowApprovingConfirmation] = useState(false);
	const [showRejectingConfirmation, setShowRejectingConfirmation] = useState(false);

	useEffect(() => {
		getIncompetent();
	}, []);

	const getIncompetent = async () => {
		try {
			setIsLoading(true);

			const response = await onGetIncompetentById(id as string);

			setIncompetentDetails(response.data.data);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			navigate('/incompetents');
		}
	};

	const onApprove = async () => {
		try {
			setIsApproving(true);
			await onApproveIncompetent(id!);
			NotificationService.success(t('COMMON.SUCCESS'), t('INCOMPETENTS.INCOMPETENT_APPROVED'));
			setShowApprovingConfirmation(false);
			setIsApproving(false);
			getIncompetent();
		} catch (error) {
			setIsApproving(false);
			NotificationService.error(t('COMMON.OPS'), t('INCOMPETENTS.INCOMPETENT_NOT_APPROVED'));
		}
	};

	const onUnlink = async () => {
		try {
			setIsUnlinking(true);
			await onUnlinkIncompetent(id!);
			NotificationService.success(t('COMMON.SUCCESS'), t('INCOMPETENTS.INCOMPETENT_UNLINKED'));
			setShowUnlinkingConfirmation(false);
			setIsUnlinking(false);
			getIncompetent();
		} catch (error) {
			setIsUnlinking(false);
			NotificationService.error(t('COMMON.OPS'), t('INCOMPETENTS.INCOMPETENT_NOT_UNLINKED'));
		}
	};

	const onReject = async (message: string) => {
		try {
			setIsRejecting(true);
			await onRejectIncompetent(id!, message);
			NotificationService.success(t('COMMON.SUCCESS'), t('INCOMPETENTS.INCOMPETENT_REJECTED'));
			setShowRejectingConfirmation(false);
			setIsRejecting(false);
			getIncompetent();
		} catch (error) {
			setIsRejecting(false);
			NotificationService.error(t('COMMON.OPS'), t('INCOMPETENTS.INCOMPETENT_NOT_REJECTED'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
			</div>

			<h3 className="fw-semibold mb-4">{t('INCOMPETENTS.INCOMPETENT_DETAILS')}</h3>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className={`sectionContainer`}>
					<div>
						<div className="d-flex align-items-center justify-content-between mb-4">
							<h5 className="fw-semibold text-primary mb-0">{t('INCOMPETENTS.INCOMPETENT_DETAILS')}</h5>
							<div className="d-flex gap-2">
								<AuthActionComponent action="minor_update">
									{incompetentDetails?.status_key == 'Pending' && (
										<>
											<Button
												color="primary"
												variant="filled"
												onClick={() => {
													setShowApprovingConfirmation(true);
												}}>
												{t('INCOMPETENTS.APPROVE')}
											</Button>
											<Button
												color="danger"
												variant="filled"
												onClick={() => {
													setShowRejectingConfirmation(true);
												}}>
												{t('INCOMPETENTS.REJECT')}
											</Button>
										</>
									)}
									{incompetentDetails?.status_key == 'Approved' && (
										<Button
											type="link"
											color="danger"
											variant="filled"
											onClick={() => {
												setShowUnlinkingConfirmation(true);
											}}>
											{t('INCOMPETENTS.UNLINK')}
										</Button>
									)}
								</AuthActionComponent>
							</div>
						</div>

						<div className={styles.data}>
							<div className={styles.data_logo} style={{ alignItems: isRTL() ? 'start' : 'center' }}>
								{incompetentDetails?.incompetent?.avatar_preview ? (
									<img src={incompetentDetails?.incompetent?.avatar_preview} />
								) : isRTL() ? (
									incompetentDetails?.incompetent?.name?.ar?.slice(0, 1)
								) : (
									incompetentDetails?.incompetent?.name?.en?.slice(0, 1)
								)}
							</div>
							<div>
								<div>
									<div className={styles.data_name}>
										<h5>{isRTL() ? incompetentDetails?.incompetent?.name?.ar : incompetentDetails?.incompetent?.name?.en}</h5>

										{incompetentDetails?.incompetent?.is_phone_verified && <Icon name="verified" />}

										<span className={`status ${StatusColors[incompetentDetails?.incompetent?.profile_status!]}`}>
											{t(`COMMON.${incompetentDetails?.incompetent?.profile_status?.toUpperCase()}`)}
										</span>
									</div>

									<div className={styles.data_connection}>
										<Row gutter={[10, 10]}>
											<Col span={12}>
												<Icon name="contact" />
												<span style={{ direction: 'ltr', fontWeight: 500 }}>
													{`+${incompetentDetails?.incompetent?.phone_country_code} ${incompetentDetails?.incompetent?.phone_number}`}
												</span>
											</Col>
											<Col span={12}>
												<Icon name="id" />
												{incompetentDetails?.incompetent?.national_id}
											</Col>
											<Col span={12}>
												<Icon name="location" />
												{incompetentDetails?.incompetent?.country?.name}
											</Col>
											<Col span={12}>
												<Icon name="country2" />
												{incompetentDetails?.incompetent?.address}
											</Col>
											<Col span={12}>
												<Icon name="heart" />
												{incompetentDetails?.incompetent?.date_of_birth
													? dayjs(incompetentDetails?.incompetent?.date_of_birth).format('YYYY-MM-DD')
													: ''}
											</Col>
											{incompetentDetails?.incompetent?.email && (
												<Col span={12}>
													<Icon name="mail" />
													{incompetentDetails?.incompetent?.email}
												</Col>
											)}
										</Row>
									</div>
								</div>
							</div>
						</div>
					</div>

					<Divider />

					<div>
						<h5 className="fw-semibold mb-4 text-primary">{t('INCOMPETENTS.GARDIAN_DETAILS')}</h5>
						<div className={styles.data}>
							<div className={styles.data_logo} style={{ alignItems: isRTL() ? 'start' : 'center' }}>
								{incompetentDetails?.guardianUser?.avatar_preview ? (
									<img src={incompetentDetails?.guardianUser?.avatar_preview} />
								) : isRTL() ? (
									incompetentDetails?.guardianUser?.name?.ar?.slice(0, 1)
								) : (
									incompetentDetails?.guardianUser?.name?.en?.slice(0, 1)
								)}
							</div>
							<div>
								<div>
									<div className={styles.data_name}>
										<h5>{isRTL() ? incompetentDetails?.guardianUser?.name?.ar : incompetentDetails?.guardianUser?.name?.en}</h5>

										{incompetentDetails?.guardianUser?.is_phone_verified && <Icon name="verified" />}

										<span className={`status ${StatusColors[incompetentDetails?.guardianUser?.profile_status!]}`}>
											{t(`COMMON.${incompetentDetails?.guardianUser?.profile_status?.toUpperCase()}`)}
										</span>
									</div>

									<div className={styles.data_connection}>
										<Row gutter={[10, 10]}>
											<Col span={12}>
												<Icon name="contact" />
												<span style={{ direction: 'ltr' }}>
													{`+${incompetentDetails?.guardianUser?.phone_country_code} ${incompetentDetails?.guardianUser?.phone_number}`}
												</span>
											</Col>
											<Col span={12}>
												<Icon name="id" />
												{incompetentDetails?.guardianUser?.national_id}
											</Col>
											<Col span={12}>
												<Icon name="location" />
												{incompetentDetails?.guardianUser?.country?.name}
											</Col>
											<Col span={12}>
												<Icon name="country2" />
												{incompetentDetails?.guardianUser?.address}
											</Col>
											<Col span={12}>
												<Icon name="heart" />
												{incompetentDetails?.guardianUser?.date_of_birth
													? dayjs(incompetentDetails?.guardianUser?.date_of_birth).format('YYYY-MM-DD')
													: ''}
											</Col>
											{incompetentDetails?.guardianUser?.email && (
												<Col span={12}>
													<Icon name="mail" />
													{incompetentDetails?.guardianUser?.email}
												</Col>
											)}
										</Row>
									</div>
								</div>
							</div>
						</div>
					</div>

					<Divider />

					<div className="d-flex justify-content-center gap-3 flex-wrap">
						{incompetentDetails?.family_id_document && (
							<div>
								<PDFViewer link={incompetentDetails?.family_id_document!} />
								<FilePreview
									className="flex-grow-1 mt-2"
									name={t('INCOMPETENTS.FAMILY_ID_DOCUMENT')}
									downloadLink={incompetentDetails?.family_id_document}
								/>
							</div>
						)}

						{incompetentDetails?.custody_order_document && (
							<div>
								<Divider />
								<PDFViewer link={incompetentDetails?.custody_order_document!} />
								<FilePreview
									className="flex-grow-1 mt-2"
									name={t('INCOMPETENTS.CUSTODY_ORDER_DOCUMENT')}
									downloadLink={incompetentDetails?.custody_order_document}
								/>
							</div>
						)}

						{incompetentDetails?.guardianship_order_document && (
							<div>
								<Divider />
								<PDFViewer link={incompetentDetails?.guardianship_order_document!} />
								<FilePreview
									className="flex-grow-1 mt-2"
									name={t('INCOMPETENTS.GUARDIANSHIP_ORDER_DOCUMENT')}
									downloadLink={incompetentDetails?.guardianship_order_document}
								/>
							</div>
						)}
					</div>
				</div>
			)}

			<ConfirmModalComponent
				open={showApprovingConfirmation}
				confirmText={t('INCOMPETENTS.APPROVE')}
				confirmType="primary"
				onDiscard={() => {
					setShowApprovingConfirmation(false);
				}}
				title={t('INCOMPETENTS.APPROVE_TITLE')}
				description={t('INCOMPETENTS.APPROVE_DESCRIPTION')}
				onConfirm={onApprove}
				isLoading={isApproving}
			/>

			<ConfirmModalComponent
				open={showUnlinkingConfirmation}
				confirmText={t('INCOMPETENTS.UNLINK')}
				onDiscard={() => {
					setShowUnlinkingConfirmation(false);
				}}
				title={t('INCOMPETENTS.UNLINK_TITLE')}
				description={t('INCOMPETENTS.UNLINK_DESCRIPTION')}
				onConfirm={onUnlink}
				isLoading={isUnlinking}
			/>

			<ConfirmModalComponent
				open={showRejectingConfirmation}
				confirmText={t('INCOMPETENTS.REJECT')}
				onDiscard={() => {
					setShowRejectingConfirmation(false);
				}}
				title={t('INCOMPETENTS.REJECT_TITLE')}
				description={t('INCOMPETENTS.REJECT_DESCRIPTION')}
				onConfirm={onReject}
				isLoading={isRejecting}
				inputMessage
			/>
		</>
	);
}

export default IncompetentViewPage;
